import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useReacaoReceptor } from '../hooks/useReacaoAoVivo';
import type { EmojiReacao } from '../hooks/useReacaoAoVivo';

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface ReacaoParticula {
  id:       number;
  emoji:    EmojiReacao;
  left:     number;
  delay:    number;
  swayDir:  'left' | 'right';
  fontSize: number;
}

interface Props {
  telaId: string;
}

const DURACAO_MS = 2_500;
let counter = 0;

// ── Componente ────────────────────────────────────────────────────────────────

/**
 * TvReacaoOverlay — recebe reações ao vivo do público mobile e exibe emojis
 * flutuando de baixo para cima sobre o conteúdo da TV.
 *
 * z-[19]: acima do conteúdo (z-0), abaixo do logo (z-10) e demais overlays.
 * Posicionamento: absolute inset-0, pointer-events-none.
 */
export default function TvReacaoOverlay({ telaId }: Props) {
  const [particulas, setParticulas] = useState<ReacaoParticula[]>([]);

  const onReacao = useCallback(({ emoji }: { emoji: EmojiReacao }) => {
    const particula: ReacaoParticula = {
      id:       ++counter,
      emoji,
      left:     8 + Math.random() * 84,
      delay:    Math.random() * 120,
      swayDir:  Math.random() > 0.5 ? 'left' : 'right',
      fontSize: 2.2 + Math.random() * 2,
    };
    setParticulas(prev => [...prev, particula]);
    setTimeout(() => {
      setParticulas(prev => prev.filter(p => p.id !== particula.id));
    }, DURACAO_MS + 200);
  }, []);

  useReacaoReceptor(telaId, onReacao);

  if (particulas.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-[19] overflow-hidden" aria-hidden="true">
      {particulas.map(p => (
        <motion.span
          key={p.id}
          style={{
            position:   'absolute',
            bottom:     '5%',
            left:       `${p.left}%`,
            fontSize:   `${p.fontSize}vw`,
            lineHeight: 1,
            userSelect: 'none',
          }}
          initial={{ y: 0, opacity: 0, scale: 0.6 }}
          animate={{
            y:       [0, '-42vh', '-85vh'],
            x:       p.swayDir === 'left' ? [0, '-3vw', '-6vw'] : [0, '3vw', '6vw'],
            opacity: [0, 1, 1, 0],
            scale:   [0.6, 1.1, 0.8],
          }}
          transition={{
            duration: DURACAO_MS / 1000,
            delay:    p.delay / 1000,
            ease:     'easeOut',
          }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </div>
  );
}
